import { useEffect } from "react";
import { QueryClient } from "react-query";
import { useChatStore } from "../stores/ChatStore";
import { getVnvAssistDialogueData } from "src/views/products/vnv-assist/services/VnvAssistServices";
import { PROPOSAL_GENERATION_PRODUCT } from "src/views/products/proposal-generation/proposalGenerationProduct";
import {
  useSetWorkflowProductConfig,
  useSetWorkflowRecentsConfig,
} from "src/views/products/workflow/hooks/useWorkflowProductConfig";

// Product configuration constants
const ATOM_ASSIST_CONFIG = {
  productKey: "atom-assist",
  productName: "Atom Assist",
  basePath: "/assistant",
};

export const setAtomAssistConfig = () => {
  const { setProductConfig } = useChatStore();
  setProductConfig(ATOM_ASSIST_CONFIG);
};

/**
 * Hook to set product configuration for Atom Assist
 * Initializes the chat store with Atom Assist product settings
 */
export const useSetAtomAssistConfig = () => {
  const { setProductConfig } = useChatStore();

  useEffect(() => {
    setProductConfig(ATOM_ASSIST_CONFIG);
  }, [setProductConfig]);
};

/**
 * Hook to set product configuration for Proposal Generation
 * Initializes the chat store with Proposal Generation product settings
 */
export const useSetProposalGenerationConfig = () =>
  useSetWorkflowProductConfig(PROPOSAL_GENERATION_PRODUCT);

/**
 * Hook to set product configuration for Proposal Generation with buildChatUrl
 * Used in recents/history pages where URL building is needed
 * @param queryClient - React Query client for fetching dialogue data
 */
export const useSetProposalGenerationRecentsConfig = (
  queryClient: QueryClient,
) => useSetWorkflowRecentsConfig(PROPOSAL_GENERATION_PRODUCT, queryClient);

// ---------------------------------------------------------------------------
// VNV Assist
// ---------------------------------------------------------------------------

const VNV_ASSIST_CONFIG = {
  productKey: "vnv-assist",
  productName: "VNV Assist",
  basePath: "/vnv-assist",
};

export const buildVnvAssistChatUrl = (queryClient: QueryClient) => {
  return async (dialogueId: number): Promise<string> => {
    try {
      const data = await queryClient.fetchQuery(
        ["vnvAssistRun", dialogueId],
        () => getVnvAssistDialogueData(dialogueId),
        { staleTime: 5 * 60 * 1000 },
      );
      const runId = data.vnv_assist_run_id;
      if (runId) {
        return `/vnv-assist/chat/${runId}/${dialogueId}`;
      }
    } catch (error) {
      console.error("VnvAssist: Failed to fetch run ID:", error);
    }
    return "/vnv-assist";
  };
};

export const useSetVnvAssistConfig = () => {
  const { setProductConfig } = useChatStore();
  useEffect(() => {
    setProductConfig(VNV_ASSIST_CONFIG);
  }, [setProductConfig]);
};

export const useSetVnvAssistRecentsConfig = (queryClient: QueryClient) => {
  const { setProductConfig, registerChatUrlBuilder } = useChatStore();
  useEffect(() => {
    setProductConfig(VNV_ASSIST_CONFIG);
    registerChatUrlBuilder(
      VNV_ASSIST_CONFIG.productKey,
      buildVnvAssistChatUrl(queryClient),
    );
  }, [setProductConfig, registerChatUrlBuilder, queryClient]);
};

// ---------------------------------------------------------------------------
// Equipment AI
// ---------------------------------------------------------------------------

// productKey matches the backend product table slug ("equipment-ai") so
// GET /chat/dialogue?product=equipment-ai returns only this product's
// dialogues. The default AllChats buildChatUrl (${basePath}/chat/${id})
// already resolves to the naked redirect route, so no override needed.
const EQUIPMENT_AI_CONFIG = {
  productKey: "equipment-ai",
  productName: "Equipment AI",
  basePath: "/products/equipment-ai",
};

export const useSetEquipmentAIRecentsConfig = () => {
  const { setProductConfig } = useChatStore();
  useEffect(() => {
    setProductConfig(EQUIPMENT_AI_CONFIG);
  }, [setProductConfig]);
};
